import type { RetirementDetails, ApplicantExpense, PeriodKey } from '@/types';
import type { ErhlDeclaration } from '@/types/ErhlDeclaration';
import { ErhlDeclarationQuestionType } from '@/helpers/const';

const frequencyToString: Record<PeriodKey, string> = {
  week: 'weekly',
  fortnight: 'fortnightly',
  month: 'monthly',
  quarter: 'quarterly',
  year: 'annually',
};

/**
 * Maps retirement details from the frontend model to an array of ERHL declaration DTOs.
 * Non-retired applicants produce rows 135-139 declarations.
 * Retired applicants produce row 140-141 declarations.
 */
export const retirementDetailsToDeclarations = (details: RetirementDetails | null | undefined): ErhlDeclaration[] => {
  if (!details) return [];

  const declarations: ErhlDeclaration[] = [];

  // Retired applicant path
  if (details.financialChangesDuringLoan !== undefined) {
    declarations.push({
      declarationQuestion: ErhlDeclarationQuestionType.FINANCIAL_CHANGES_DURING_LOAN,
      valueBoolean: details.financialChangesDuringLoan,
      description: details.financialChangesDuringLoanDescription || undefined,
    });
    return declarations;
  }

  // Non-retired applicant path
  declarations.push({
    declarationQuestion: ErhlDeclarationQuestionType.INCOME_CHANGES_BEFORE_RETIREMENT,
    valueBoolean: details.incomeChangesBeforeRetirement,
    description: details.incomeChangesBeforeRetirementDescription || undefined,
  });

  declarations.push({
    declarationQuestion: ErhlDeclarationQuestionType.RETIREMENT_INCOME_IMPACT,
    description: details.retirementIncomeImpact || undefined,
  });

  declarations.push({
    declarationQuestion: ErhlDeclarationQuestionType.EXPECTED_RETIREMENT_INCOME,
    valueNumeric: details.expectedRetirementIncome?.amount ?? 0,
    valueString: frequencyToString[details.expectedRetirementIncome?.frequency ?? 'month'],
  });

  return declarations;
};

/**
 * Maps "after retirement" expense change answers to ERHL declaration DTOs.
 * Produces `living_expenses_change_after_retirement` for the direction,
 * and `expense_change_after_retirement` for the amount, frequency, and description
 * when increase or decrease is selected.
 */
export const expenseDetailsToDeclarations = (expense: ApplicantExpense | null | undefined): ErhlDeclaration[] => {
  const retirementChange = expense?.retirementExpenseChange as string | undefined;
  if (!retirementChange) return [];

  const declarations: ErhlDeclaration[] = [];

  const changeDirection = retirementChange === 'noChange' ? 'no_change' : retirementChange;
  declarations.push({
    declarationQuestion: ErhlDeclarationQuestionType.LIVING_EXPENSES_CHANGE_AFTER_RETIREMENT,
    valueString: changeDirection,
  });

  if (retirementChange !== 'noChange') {
    const changeAmount = expense?.retirementExpenseChangeAmount as { amount: number; frequency: string } | undefined;

    declarations.push({
      declarationQuestion: ErhlDeclarationQuestionType.EXPENSE_CHANGE_AFTER_RETIREMENT,
      valueNumeric: changeAmount?.amount ?? 0,
      valueString: frequencyToString[(changeAmount?.frequency as PeriodKey) ?? 'month'],
      description: (expense?.retirementExpenseChangeReason as string) || undefined,
    });
  }

  return declarations;
};

/**
 * Maps the "financial obligations" answer from the Assets & Liabilities step
 * to an ERHL declaration DTO (`has_financial_obligations`).
 */
export const financialObligationsToDeclarations = (
  financials: { hasFinancialObligations?: boolean; financialObligationsDescription?: string } | null | undefined,
): ErhlDeclaration[] => {
  if (financials?.hasFinancialObligations === undefined) return [];

  return [
    {
      declarationQuestion: ErhlDeclarationQuestionType.HAS_FINANCIAL_OBLIGATIONS,
      valueBoolean: financials.hasFinancialObligations,
      description: financials.hasFinancialObligations
        ? financials.financialObligationsDescription || undefined
        : undefined,
    },
  ];
};
